import { useEffect } from 'react';
import { Descriptions, Form, Input, Modal, Select } from 'antd';
import { useCreateServiceRequest } from '@hooks/useService';
import type {
  InstalledEquipmentItem,
  ServicePriority,
  ServiceRequestType,
} from '@/types/service';

const MD_TYPE_OPTIONS: { value: ServiceRequestType; label: string }[] = [
  { value: 'Warranty', label: 'Гарантійний' },
  { value: 'PostWarranty', label: 'Післягарантійний' },
  { value: 'Maintenance', label: 'Обслуговування' },
  { value: 'Installation', label: 'Встановлення' },
  { value: 'Calibration', label: 'Калібрування' },
];

// SLA рахує сервер від пріоритету: Critical 4 год, High 24 год, Normal 48 год, Low 5 днів
const MD_PRIORITY_OPTIONS: { value: ServicePriority; label: string }[] = [
  { value: 'Critical', label: 'Критичний (SLA 4 год)' },
  { value: 'High', label: 'Високий (SLA 24 год)' },
  { value: 'Normal', label: 'Нормальний (SLA 48 год)' },
  { value: 'Low', label: 'Низький (SLA 5 днів)' },
];

interface CreateServiceRequestModalProps {
  // Заявка створюється на конкретне обладнання; клієнт береться з нього
  equipment: InstalledEquipmentItem | null;
  onClose: () => void;
  onCreated: (mdRequestId: string) => void;
}

export default function CreateServiceRequestModal({
  equipment,
  onClose,
  onCreated,
}: CreateServiceRequestModalProps) {
  const [mdForm] = Form.useForm();
  const mdCreateMutation = useCreateServiceRequest();

  useEffect(() => {
    if (!equipment) return;
    mdForm.resetFields();
    mdForm.setFieldsValue({
      type: equipment.isUnderWarranty ? 'Warranty' : 'PostWarranty',
      priority: 'Normal',
    });
  }, [equipment, mdForm]);

  const mdHandleSubmit = async () => {
    if (!equipment) return;
    const mdValues = await mdForm.validateFields();
    const mdRequestId = await mdCreateMutation.mutateAsync({
      type: mdValues.type,
      priority: mdValues.priority,
      customerId: equipment.customerId,
      installedEquipmentId: equipment.id,
      description: mdValues.description.trim(),
    });
    onCreated(mdRequestId);
  };

  return (
    <Modal
      title="Нова сервісна заявка"
      open={!!equipment}
      onOk={mdHandleSubmit}
      onCancel={onClose}
      confirmLoading={mdCreateMutation.isPending}
      okText="Створити"
      cancelText="Скасувати"
      width={600}
    >
      {equipment && (
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Обладнання">
            {equipment.serialNumber} — {equipment.productName}
          </Descriptions.Item>
          <Descriptions.Item label="Клієнт">{equipment.customerName}</Descriptions.Item>
          <Descriptions.Item label="Гарантія">
            {equipment.isUnderWarranty ? 'Діє' : 'Закінчилась'}
          </Descriptions.Item>
        </Descriptions>
      )}
      <Form form={mdForm} layout="vertical">
        <Form.Item
          name="type"
          label="Тип заявки"
          rules={[{ required: true, message: 'Оберіть тип заявки' }]}
        >
          <Select options={MD_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="priority"
          label="Пріоритет"
          rules={[{ required: true, message: 'Оберіть пріоритет' }]}
        >
          <Select options={MD_PRIORITY_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="description"
          label="Опис проблеми"
          rules={[
            { required: true, whitespace: true, message: 'Опишіть проблему' },
            { max: 2000, message: 'Не більше 2000 символів' },
          ]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
